import { AxiosInstance } from "axios";
import {
  AdditionalService,
  Category,
  Interest,
  HierarchicalGroup,
  Highlights,
  ImportantInformation,
  Included,
  Market,
  BookingQuestion,
  Pax,
  OperationalCompany,
  OperationalOffice,
  OptionName,
} from "@/types/generated/ExperienceMasterDataApi";
import { useAuthStore } from "@/features/auth/useAuthStore";
import { createHttpInstance } from "@/features/core-shared/utils/http/http";
import { AvailableLanguage } from "@/types/Language";
import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";
import { Options } from "@/types/Http";
import {
  BookingQuestionTranslationPayload,
  LanguageCode,
} from "@/features/masterdata-management/operation-content/types/BookingQuestionTranslationTypes";

export type ExperienceMasterDataItem = Highlights | Included | ImportantInformation | AdditionalService;
export type ExperiencePremadeItem = Highlights | Included | ImportantInformation;
export type ExperienceMasterData = ExperienceMasterDataItem[];

export type ExperienceMasterDataEndpoint = "highlights" | "included" | "important-information" | "additional-services";

export interface PremadeItemCreate {
  name: string;
  code: string;
  language_code: "en";
  hierarchical_group_code?: string;
}

export interface PremadeItemTranslate {
  code: string;
  languages: {
    language_code: string;
    to_be_translated: boolean;
  }[];
}

export interface HierarchicalGroupCreate {
  language_code: "en";
  code: string;
  name: string;
  parent_code: string;
}

export interface HierarchicalGroupCreateV2 {
  group: HierarchicalGroupCreate;
  manual_languages: string[];
  automatic_languages: string[];
}

export interface BookingQuestionCreate {
  code: string;
  question: string;
  category: "MAIN" | "DATETIME" | "PERSONAL" | "TRAVEL" | "EXTRA";
  answer_type: "TEXT" | "PARAGRAPH" | "RADIO" | "DATE" | "TIME" | "NUMBER";
  apply_policy: "BOOKING" | "CONFIGURABLE" | "PARTICIPANT";
  description?: string;
  placeholder?: string;
  is_personal_information: boolean;
  options?: string[];
  language_code: string;
}

type ExperienceMasterDataApi = {
  _axiosInstance: AxiosInstance;
  getCategories: ReturnType<typeof getCategories>;
  getInterests: ReturnType<typeof getInterests>;
  getMarkets: ReturnType<typeof getMarkets>;
  createPremadeHighlight: ReturnType<typeof createPremadeHighlight>;
  createMultipleItemsV2: ReturnType<typeof createMultipleItemsV2>;
  translatePremadeHighlight: ReturnType<typeof translatePremadeHighlight>;
  getHierarchicalGroups: ReturnType<typeof getHierarchicalGroups>;
  getPremades: ReturnType<typeof getPremades>;
  getOptionNames: ReturnType<typeof getOptionNames>;
  createHierarchicalGroup: ReturnType<typeof createHierarchicalGroup>;
  createHierarchicalGroupV2: ReturnType<typeof createHierarchicalGroupV2>;
  updatePremade: ReturnType<typeof updatePremade>;
  archivePremade: ReturnType<typeof archivePremade>;
  unarchivePremade: ReturnType<typeof unarchivePremade>;
  archiveHierarchicalGroup: ReturnType<typeof archiveHierarchicalGroup>;
  unarchiveHierarchicalGroup: ReturnType<typeof unarchiveHierarchicalGroup>;
  updateHierarchicalGroup: ReturnType<typeof updateHierarchicalGroup>;
  archiveBookingQuestion: ReturnType<typeof archiveBookingQuestion>;
  unarchiveBookingQuestion: ReturnType<typeof unarchiveBookingQuestion>;
  getBookingQuestions: ReturnType<typeof getBookingQuestions>;
  updateBookingQuestion: ReturnType<typeof updateBookingQuestion>;
  createBookingQuestion: ReturnType<typeof createBookingQuestion>;
  getBookingQuestionTranslations: ReturnType<typeof getBookingQuestionTranslations>;
  getBookingQuestionByCodeAndLanguage: ReturnType<typeof getBookingQuestionByCodeAndLanguage>;
  createBookingQuestionTranslation: ReturnType<typeof createBookingQuestionTranslation>;
  updateBookingQuestionTranslation: ReturnType<typeof updateBookingQuestionTranslation>;
  getSupportedLanguages: ReturnType<typeof getSupportedLanguages>;
  getPax: ReturnType<typeof getPax>;
  getCompanies: ReturnType<typeof getCompanies>;
  getOffices: ReturnType<typeof getOffices>;
  getAllOffices: ReturnType<typeof getAllOffices>;
};

function getPremades(http: AxiosInstance) {
  return async (
    endpoint: ExperienceMasterDataEndpoint,
    params?: { code?: string; language_code?: AvailableLanguage; id?: string },
    options?: Options
  ) => {
    const { config } = options ?? {};

    const filtersToApply = [];

    if (params?.language_code) {
      filtersToApply.push(builder.eq("language_code", params.language_code));
    }

    if (params?.code) {
      filtersToApply.push(builder.eq("code", params.code));
    }

    if (params?.id) {
      filtersToApply.push(builder.eq("id", params.id));
    }

    const filters = filtersToApply.length ? emit(builder.and(...filtersToApply)) : undefined;

    const { data } = await http.get<ExperienceMasterData>(endpoint, {
      params: { filters },
      ...config,
    });

    return { data };
  };
}

function getOptionNames(http: AxiosInstance) {
  return async (params?: { language_code?: AvailableLanguage }, options?: Options) => {
    const { config } = options ?? {};

    const filtersToApply = [];

    if (params?.language_code) {
      filtersToApply.push(builder.eq("language_code", params.language_code));
    }

    const filters = filtersToApply.length ? emit(builder.and(...filtersToApply)) : undefined;
    const { data } = await http.get<OptionName[]>("option-names", {
      params: { filters },
      ...config,
    });

    return { data };
  };
}

function getHierarchicalGroups(http: AxiosInstance) {
  return async (params?: { code?: string; language_code?: AvailableLanguage }, options?: Options) => {
    const { config } = options ?? {};

    const filtersToApply = [];

    if (params?.language_code) {
      filtersToApply.push(builder.eq("language_code", params.language_code));
    }

    if (params?.code) {
      filtersToApply.push(builder.eq("code", params.code));
    }

    const filters = filtersToApply.length ? emit(builder.and(...filtersToApply)) : undefined;

    const { data } = await http.get<HierarchicalGroup[]>("hierarchical-groups", {
      params: { filters },
      ...config,
    });

    return { data };
  };
}

function createHierarchicalGroup(http: AxiosInstance) {
  return async (item: HierarchicalGroupCreate, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.post<HierarchicalGroup>("hierarchical-groups", item, config);

    return { data };
  };
}

function createHierarchicalGroupV2(http: AxiosInstance) {
  return async (item: HierarchicalGroupCreateV2, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.post<HierarchicalGroup>("hierarchical-groups", item, {
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": "vnd.experience-master-data-offer-service.v2",
      },
    });

    return { data };
  };
}

function updatePremade(http: AxiosInstance) {
  return async (type: ExperienceMasterDataEndpoint, item: Partial<ExperienceMasterDataItem>, options?: Options) => {
    const { config } = options ?? {};

    // removing the id since it will make the PUT fail
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...itemWithoutId } = item;
    await http.put<ExperienceMasterDataItem>(`${type}/${item.id}`, itemWithoutId, config);
  };
}

function updateBookingQuestion(http: AxiosInstance) {
  return async (item: Partial<BookingQuestion>, options?: Options) => {
    const { config } = options ?? {};

    // removing the id since it will make the PUT fail
    const { id: _id, ...itemWithoutId } = item;
    await http.put<BookingQuestion>(`booking-questions/${item.id}`, itemWithoutId, config);
  };
}

function createBookingQuestion(http: AxiosInstance) {
  return async (item: BookingQuestionCreate, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.post<BookingQuestion>("booking-questions", item, config);

    return { data };
  };
}

function archivePremade(http: AxiosInstance) {
  return async (type: ExperienceMasterDataEndpoint, code: string, options?: Options) => {
    const { config } = options ?? {};

    await http.post(`${type}/${code}/archive`, {}, config);
  };
}

function unarchivePremade(http: AxiosInstance) {
  return async (type: ExperienceMasterDataEndpoint, code: string, options?: Options) => {
    const { config } = options ?? {};

    await http.post(`${type}/${code}/unarchive`, {}, config);
  };
}

function updateHierarchicalGroup(http: AxiosInstance) {
  return async (item: Partial<HierarchicalGroup>, options?: Options) => {
    const { config } = options ?? {};

    // removing the id since it will make the PUT fail
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...itemWithoutId } = item;
    await http.put<HierarchicalGroup>(`hierarchical-groups/${item.id}`, { ...itemWithoutId }, config);
  };
}

function archiveHierarchicalGroup(http: AxiosInstance) {
  return async (code: string, options?: Options) => {
    const { config } = options ?? {};

    await http.post(`hierarchical-groups/${code}/archive`, {}, config);
  };
}

function unarchiveHierarchicalGroup(http: AxiosInstance) {
  return async (code: string, options?: Options) => {
    const { config } = options ?? {};

    await http.post(`hierarchical-groups/${code}/unarchive`, {}, config);
  };
}

function archiveBookingQuestion(http: AxiosInstance) {
  return async (code: string, options?: Options) => {
    const { config } = options ?? {};

    await http.post(`booking-questions/${code}/archive`, {}, config);
  };
}

function unarchiveBookingQuestion(http: AxiosInstance) {
  return async (code: string, options?: Options) => {
    const { config } = options ?? {};

    await http.post(`booking-questions/${code}/unarchive`, {}, config);
  };
}

function createPremadeHighlight(http: AxiosInstance) {
  return async (endpoint: ExperienceMasterDataEndpoint, item: PremadeItemCreate, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.post<Highlights>(endpoint, item, config);

    return { data };
  };
}

function createMultipleItemsV2(http: AxiosInstance) {
  return async (
    endpoint: ExperienceMasterDataEndpoint,
    content: {
      items: PremadeItemCreate[];
      manual_languages: string[];
      automatic_languages: string[];
    },
    options?: Options
  ) => {
    const { config } = options ?? {};

    const { data } = await http.post(endpoint, content, {
      ...config,
      headers: {
        ...config?.headers,
        "accept-version": "vnd.experience-master-data-offer-service.v2",
      },
    });

    return { data };
  };
}

function translatePremadeHighlight(http: AxiosInstance) {
  return async (endpoint: ExperienceMasterDataEndpoint, item: PremadeItemTranslate, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.post(endpoint + "/translate", item, config);

    return { data };
  };
}

function getMarkets(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(builder.eq("language_code", config?.params.language_code || "en"));

    const { data } = await http.get<Market[]>("markets", {
      params: { filters },
      ...config,
    });

    return { data };
  };
}

function getInterests(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(builder.eq("language_code", config?.params.language_code || "en"));

    const { data } = await http.get<Interest[]>("interests", {
      params: { filters },
      ...config,
    });

    return { data };
  };
}

function getCategories(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(builder.eq("language_code", config?.params.language_code || "en"));

    const { data } = await http.get<Category[]>("categories", {
      params: { filters },
      ...config,
    });

    return { data };
  };
}

function getBookingQuestions(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options || {};

    const { data } = await http.get<BookingQuestion[]>(`/booking-questions`, {
      ...config,
      params: {
        filters: emit(builder.eq("language_code", "en")),
      },
    });

    return { data };
  };
}

function getPax(http: AxiosInstance) {
  return async (paxType: Pax["type"], options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(
      builder.and(builder.eq("type", paxType), builder.eq("language_code", config?.params.language_code || "en"))
    );

    const { data } = await http.get<Pax[]>("/pax", {
      ...config,
      params: { filters },
    });

    return { data };
  };
}

function getCompanies(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.get<OperationalCompany[]>("operational-companies", {
      ...config,
    });

    return { data };
  };
}

function getOffices(http: AxiosInstance) {
  return async (companyCode: string | string[], options?: Options) => {
    const { config } = options ?? {};

    if (Array.isArray(companyCode) && companyCode.length === 0) {
      const data: OperationalOffice[] = [];
      return { data };
    }

    const filters = emit(
      Array.isArray(companyCode) ? builder.in("company_code", companyCode) : builder.eq("company_code", companyCode)
    );

    const { data } = await http.get<OperationalOffice[]>("/operational-offices", {
      ...config,
      params: { filters },
    });

    return { data };
  };
}

function getAllOffices(http: AxiosInstance) {
  return async (options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.get<OperationalOffice[]>("/operational-offices", {
      ...config,
    });

    return { data };
  };
}

function getBookingQuestionTranslations(http: AxiosInstance) {
  return async (code: string, options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(builder.eq("code", code));

    const { data } = await http.get<BookingQuestion[]>("/booking-questions", {
      ...config,
      params: { filters },
    });

    return { data };
  };
}

function getBookingQuestionByCodeAndLanguage(http: AxiosInstance) {
  return async (code: string, languageCode: LanguageCode, options?: Options) => {
    const { config } = options ?? {};

    const filters = emit(builder.and(builder.eq("code", code), builder.eq("language_code", languageCode)));

    const { data } = await http.get<BookingQuestion[]>("/booking-questions", {
      ...config,
      params: { filters },
    });

    return { data: data.length > 0 ? data[0] : null };
  };
}

function createBookingQuestionTranslation(http: AxiosInstance) {
  return async (payload: BookingQuestionTranslationPayload, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.post<BookingQuestion>("/booking-questions", payload, config);

    return { data };
  };
}

function updateBookingQuestionTranslation(http: AxiosInstance) {
  return async (id: string, payload: BookingQuestionTranslationPayload, options?: Options) => {
    const { config } = options ?? {};

    const { data } = await http.put<BookingQuestion>(`/booking-questions/${id}`, payload, config);

    return { data };
  };
}

function getSupportedLanguages(_http: AxiosInstance) {
  return async (_options?: Options) => {
    // For now, return the available languages from our constants
    // This could be replaced with an actual API call if the backend provides this endpoint
    const { AVAILABLE_LANGUAGES } = await import("@/types/Language");

    return { data: [...AVAILABLE_LANGUAGES] as LanguageCode[] };
  };
}

/**
 * Experience Master Data Service interface
 * @returns _axiosInstance - Original axios instance, to be used only for internal purpose
 * @returns get - Public method for get requests, TS already configured
 */
export function useExperienceMasterDataApi(): ExperienceMasterDataApi {
  const config = useRuntimeConfig();
  const auth = useAuthStore();

  const axiosInstance = createHttpInstance({
    config: {
      baseURL: config.public.EXPERIENCE_MASTER_DATA_SERVICE_BASE_URL,
      headers: {
        "accept-version": "vnd.experience-master-data-offer-service.v1",
      },
    },
    auth,
  });

  return {
    _axiosInstance: axiosInstance,
    getCategories: getCategories(axiosInstance),
    getInterests: getInterests(axiosInstance),
    getMarkets: getMarkets(axiosInstance),
    createPremadeHighlight: createPremadeHighlight(axiosInstance),
    createMultipleItemsV2: createMultipleItemsV2(axiosInstance),
    translatePremadeHighlight: translatePremadeHighlight(axiosInstance),
    getHierarchicalGroups: getHierarchicalGroups(axiosInstance),
    createHierarchicalGroup: createHierarchicalGroup(axiosInstance),
    createHierarchicalGroupV2: createHierarchicalGroupV2(axiosInstance),
    getPremades: getPremades(axiosInstance),
    getOptionNames: getOptionNames(axiosInstance),
    updatePremade: updatePremade(axiosInstance),
    archivePremade: archivePremade(axiosInstance),
    unarchivePremade: unarchivePremade(axiosInstance),
    archiveHierarchicalGroup: archiveHierarchicalGroup(axiosInstance),
    unarchiveHierarchicalGroup: unarchiveHierarchicalGroup(axiosInstance),
    updateHierarchicalGroup: updateHierarchicalGroup(axiosInstance),
    archiveBookingQuestion: archiveBookingQuestion(axiosInstance),
    unarchiveBookingQuestion: unarchiveBookingQuestion(axiosInstance),
    getBookingQuestions: getBookingQuestions(axiosInstance),
    updateBookingQuestion: updateBookingQuestion(axiosInstance),
    createBookingQuestion: createBookingQuestion(axiosInstance),
    getBookingQuestionTranslations: getBookingQuestionTranslations(axiosInstance),
    getBookingQuestionByCodeAndLanguage: getBookingQuestionByCodeAndLanguage(axiosInstance),
    createBookingQuestionTranslation: createBookingQuestionTranslation(axiosInstance),
    updateBookingQuestionTranslation: updateBookingQuestionTranslation(axiosInstance),
    getSupportedLanguages: getSupportedLanguages(axiosInstance),
    getPax: getPax(axiosInstance),
    getCompanies: getCompanies(axiosInstance),
    getOffices: getOffices(axiosInstance),
    getAllOffices: getAllOffices(axiosInstance),
  };
}
